import { useEffect, useId, useRef, type ReactNode } from 'react'
import { createPortal } from 'react-dom'

export interface DialogProps {
  readonly open: boolean
  readonly onClose: () => void
  readonly title: string
  readonly description?: string
  readonly children?: ReactNode
  /** Rodapé (ações). */
  readonly footer?: ReactNode
}

const FOCUSABLE =
  'a[href],button:not([disabled]),textarea:not([disabled]),input:not([disabled]),select:not([disabled]),[tabindex]:not([tabindex="-1"])'

/**
 * Modal acessível: portal, overlay, `role="dialog"` + `aria-modal`, rótulo/descrição por id, foco preso
 * dentro do diálogo (Tab cicla), Escape fecha, clique no overlay fecha, foco volta ao gatilho ao fechar e
 * o scroll do body trava enquanto aberto. Respeita prefers-reduced-motion.
 */
export function Dialog({ open, onClose, title, description, children, footer }: DialogProps) {
  const titleId = useId()
  const descriptionId = useId()
  const panelRef = useRef<HTMLDivElement | null>(null)
  const previouslyFocused = useRef<HTMLElement | null>(null)

  useEffect(() => {
    if (!open) {
      return
    }

    previouslyFocused.current = document.activeElement as HTMLElement | null
    const body = document.body
    const previousOverflow = body.style.overflow
    body.style.overflow = 'hidden'

    const panel = panelRef.current
    // Foca o primeiro elemento focável do diálogo (ou o próprio painel).
    const first = panel?.querySelector<HTMLElement>(FOCUSABLE)
    ;(first ?? panel)?.focus()

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault()
        onClose()
        return
      }
      if (event.key !== 'Tab' || panel === null) {
        return
      }
      const focusable = Array.from(panel.querySelectorAll<HTMLElement>(FOCUSABLE))
      if (focusable.length === 0) {
        event.preventDefault()
        return
      }
      const firstEl = focusable[0]
      const lastEl = focusable[focusable.length - 1]
      const active = document.activeElement
      if (event.shiftKey && active === firstEl) {
        event.preventDefault()
        lastEl?.focus()
      } else if (!event.shiftKey && active === lastEl) {
        event.preventDefault()
        firstEl?.focus()
      }
    }

    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('keydown', onKeyDown)
      body.style.overflow = previousOverflow
      previouslyFocused.current?.focus()
    }
  }, [open, onClose])

  if (!open || typeof document === 'undefined') {
    return null
  }

  return createPortal(
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      // Clicar FORA do painel fecha. Testar contra o painel (e não currentTarget) porque o clique
      // acerta o div de overlay, que é irmão do painel — não o container.
      onMouseDown={(event) => {
        if (panelRef.current !== null && !panelRef.current.contains(event.target as Node)) {
          onClose()
        }
      }}
    >
      <div
        aria-hidden="true"
        className="absolute inset-0 bg-black/40 motion-safe:transition-opacity"
      />
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={description !== undefined ? descriptionId : undefined}
        tabIndex={-1}
        className="relative z-10 flex w-full max-w-md flex-col gap-4 rounded-lg border border-border-default bg-background-elevated p-5 shadow-xl"
      >
        <div className="flex flex-col gap-1">
          <h2 id={titleId} className="text-base font-semibold text-foreground-primary">
            {title}
          </h2>
          {description !== undefined ? (
            <p id={descriptionId} className="text-sm text-foreground-muted">
              {description}
            </p>
          ) : null}
        </div>
        {children !== undefined ? (
          <div className="text-sm text-foreground-secondary">{children}</div>
        ) : null}
        {footer !== undefined ? <div className="flex justify-end gap-2">{footer}</div> : null}
      </div>
    </div>,
    document.body,
  )
}
